import { getDeliveryFeeMatrix } from "../api";
import { getProductsByListOfIds } from "../../products/api";

export const parseWeight = (value) => {
  const weight = Number(String(value ?? "").trim());
  return Number.isFinite(weight) && weight >= 0 ? weight : 0;
};

export const parseDeliveryFeeMatrix = (data) => {
  const read = (key) => {
    const value = data && data[key] !== null && data[key] !== "" ? Number(data[key]) : NaN;
    return Number.isFinite(value) ? value : null;
  };
  const matrix = {
    baseRate: read("baseRate"),
    excessRate: read("excessRate"),
    baseWeight: read("baseWeight"),
    excessWeight: read("excessWeight"),
  };
  if (
    Object.values(matrix).some((value) => value === null) ||
    matrix.baseRate < 0 ||
    matrix.excessRate < 0 ||
    matrix.baseWeight < 0 ||
    matrix.excessWeight <= 0
  ) {
    throw new Error("Invalid delivery fee matrix");
  }
  return matrix;
};

export const feeForWeight = (matrix, totalWeightKg) => {
  const grams = (kg) => Math.round(kg * 1000);
  const excessGrams = grams(totalWeightKg) - grams(matrix.baseWeight);
  const steps =
    excessGrams > 0 ? Math.ceil(excessGrams / grams(matrix.excessWeight)) : 0;
  return matrix.baseRate + steps * matrix.excessRate;
};

const MATRIX_CACHE_KEY = "deliveryFeeMatrix";
const MATRIX_CACHE_MS = 60 * 60 * 1000;
let matrixRequest = null;

const readCachedMatrix = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(MATRIX_CACHE_KEY));
    if (cached && Date.now() - cached.fetchedAt < MATRIX_CACHE_MS) {
      return parseDeliveryFeeMatrix(cached.matrix);
    }
  } catch (error) {
    // Missing, expired or unreadable: fetch it again.
  }
  return null;
};

// The matrix is fetched at most once an hour; every calculation in between,
// across reloads and tabs, uses the copy in localStorage.
export const getMatrix = () => {
  const cached = readCachedMatrix();
  if (cached) return Promise.resolve(cached);
  if (!matrixRequest) {
    matrixRequest = getDeliveryFeeMatrix()
      .then((data) => {
        const matrix = parseDeliveryFeeMatrix(data);
        try {
          localStorage.setItem(
            MATRIX_CACHE_KEY,
            JSON.stringify({ matrix, fetchedAt: Date.now() })
          );
        } catch (error) {
          // Storage unavailable: the matrix is still used for this calculation.
        }
        return matrix;
      })
      .finally(() => {
        matrixRequest = null;
      });
  }
  return matrixRequest;
};

const toProductList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    for (const key of ["products", "object", "content"]) {
      if (key in data) return toProductList(data[key]);
    }
  }
  throw new Error("Product response does not contain a product list.");
};

const fetchWeights = async (productIds) => {
  const ids = [...new Set(productIds)];
  if (ids.length === 0) return {};
  const products = toProductList(await getProductsByListOfIds(ids));
  return Object.fromEntries(
    products.map((item) => {
      const product = item.product || item;
      return [String(product.id), parseWeight(product.weight ?? item.weight)];
    })
  );
};

export const calculateDeliveryFee = async (items) => {
  const known = items.filter((item) => item.weight != null);
  const missing = items
    .filter((item) => item.weight == null)
    .map((item) => String(item.id));
  const [matrix, fetched] = await Promise.all([getMatrix(), fetchWeights(missing)]);
  const weights = {
    ...fetched,
    ...Object.fromEntries(
      known.map((item) => [String(item.id), parseWeight(item.weight)])
    ),
  };
  const totalWeight = items.reduce(
    (sum, item) => sum + (weights[String(item.id)] ?? 0) * item.quantity,
    0
  );
  return feeForWeight(matrix, totalWeight);
};
